import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { HiChevronDown } from "react-icons/hi2";


const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 190px;
  margin-top:  -15px !important;
  border: 1px solid rgba(31, 31, 31, 1);
  border-radius: 6px;
  background-color: var(--primary-color);
  transition: border-color 0.2s ease;
  cursor: pointer;
  user-select: none;
  
  &:hover,
  &:active {
    border-color: white;
  }
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  font-size: 13px;
  color: var(--text-color);
`;

const DropdownList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: var(--primary-color);
  border: 1px solid rgba(31, 31, 31, 1);
  border-radius: 6px;
  list-style: none;
  margin: 0;
  font-size: 13px;

  gap: 0px;
  display: flex;
  flex-direction: column;
  font-family: 'Onest', sans-serif;
  font-weight: 500;
  padding: 0;
  padding-top: 6px;
  padding-bottom: 6px;
  max-height: 180px;
  overflow-y: auto;
  z-index: 50;
`;

const DropdownItem = styled.li<{ selected?: boolean }>`
  font-size: 13px;
  padding: 0;
  padding-left: 12px;
  padding-right: 12px;
  padding-top: 6px;
  padding-bottom: 6px;
  margin: 0;
  color: ${({ selected }) => (selected ? "var(--text-active-color)" : "var(--text-color)")};
  background-color: ${({ selected }) => (selected ? "rgba(255,255,255,0.05)" : "transparent")};
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }
`;

const Chevron = styled(HiChevronDown)<{ open: boolean }>`
  transition: transform 0.25s ease;
  transform: rotate(${({ open }) => (open ? "180deg" : "0deg")});
  color: var(--text-color);
`;

type DropdownBoxProps = {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

export const DropdownBox: React.FC<DropdownBoxProps> = ({
  options,
  value = "",
  onChange,
  placeholder = "Выберите...",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
  };

  // ✅ закрытие при клике вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  return (
    <DropdownWrapper ref={ref}>
      <DropdownHeader onClick={() => setOpen((o) => !o)}>
        <span data-placeholder={!value}>{value || placeholder}</span>
        <Chevron size={16} open={open} />
      </DropdownHeader>

      {open && (
        <DropdownList>
          {options.map((opt) => (
            <DropdownItem
              key={opt}
              selected={opt === value}
              onClick={() => handleSelect(opt)}
            >
              {opt}
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </DropdownWrapper>
  );
};
type DropdownBoxSearchProps = {
  options: string[];
  value?: string;
  onChange: (val: string) => void;
  placeholder?: string;
};

const SearchDropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 190px;
  border: 1px solid rgba(31, 31, 31, 1);
  border-radius: 6px;
  background-color: var(--primary-color);
  cursor: text;
  user-select: none;
`;

const SearchDropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: var(--text-color);
  font-family: 'Onest', sans-serif;
  font-weight: 500;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SearchDropdownList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: var(--primary-color);
  border: 1px solid rgba(31, 31, 31, 1);
  border-radius: 6px;
  list-style: none;
  margin: 0;
  padding-top: 6px;
  padding-bottom: 6px;
  max-height: 180px;
  overflow-y: auto;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const SearchDropdownItem = styled.li<{ selected?: boolean; disabled?: boolean }>`
  font-size: 13px;
  padding: 6px 12px;
  color: ${({ selected }) =>
    selected ? "var(--text-active-color)" : "var(--text-color)"};
  background-color: ${({ selected }) =>
    selected ? "rgba(255,255,255,0.05)" : "transparent"};
  transition: background-color 0.2s ease, color 0.2s ease;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};

  &:hover {
    background-color: ${({ disabled }) =>
      disabled ? "transparent" : "rgba(255,255,255,0.08)"};
  }
`;

const SearchChevron = styled(HiChevronDown)<{ open: boolean }>`
  transition: transform 0.25s ease;
  transform: rotate(${({ open }) => (open ? "180deg" : "0deg")});
  color: var(--text-color);
`;


const Wrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 190px;
`;

const Header = styled.div<{ open: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-color: var(--primary-color);
  border: 1px solid rgba(31, 31, 31, 1);
  border-radius: 6px;
  cursor: text;
  user-select: none;

  &:hover {
    border-color: white;
  }
`;

const Input = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-color);
  font-size: 13px;
  font-family: 'Onest', sans-serif;
  font-weight: 500;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;



const List = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  background-color: var(--primary-color);
  border: 1px solid rgba(31, 31, 31, 1);
  border-radius: 6px;
  list-style: none;
  margin: 0;
  padding: 6px 0;
  z-index: 50;
`;





const Item = styled.li<{ selected?: boolean; disabled?: boolean }>`
  padding: 6px 12px;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  color: ${({ selected }) => (selected ? "var(--text-active-color)" : "var(--text-color)")};
  font-size: 13px;
  font-family: 'Onest', sans-serif;
  font-weight: 500;
  transition: background 0.2s;

  &:hover {
    background-color: ${({ disabled }) =>
      disabled ? "transparent" : "rgba(255,255,255,0.08)"};
  }
`;

export const DropdownBoxSearch: React.FC<DropdownBoxSearchProps> = ({
  options,
  value = "",
  onChange,
  placeholder = "Выберите...",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value);
  const ref = useRef<HTMLDivElement>(null);

  const handleSelect = (val: string) => {
    onChange(val);
    setSearch(val);
    setOpen(false);
  };

  const filtered = options.filter((opt) =>
    opt.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <Wrapper ref={ref}>
      <Header open={open} onClick={() => setOpen(true)}>
        <Input
          placeholder={placeholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        <Chevron size={16} open={open} />
      </Header>

      {open && (
        <List>
          {filtered.length ? (
            filtered.map((opt) => (
              <Item
                key={opt}
                selected={opt === value}
                onClick={() => handleSelect(opt)}
              >
                {opt}
              </Item>
            ))
          ) : (
            <Item disabled>Ничего не найдено</Item>
          )}
        </List>
      )}
    </Wrapper>
  );
};